/**
 * NAV-APP
 * Container for account application
 * First level of navigation (with visible menu)
 */

import { connect } from "react-redux"
import { motion, useReducedMotion } from "framer-motion"
import { withTranslation, WithTranslation } from "react-i18next"
import NavItem from "@/components/nav-item"
import {
  faBuilding,
  faCaretDown,
  faChartBar,
  faChevronLeft,
  faCity,
  faClipboardList,
  faCog,
  faCrown,
  faFileAlt,
  faFilm,
  faHome,
  faListCheck,
  faLocationDot,
  faPaperPlane,
  faCircleCheck,
  faSliders,
  faTasks,
  faUser,
  faUserFriends,
  faUsers,
  faUserShield,
  IconDefinition,
  faList,
  faShieldHalved
} from "@fortawesome/free-solid-svg-icons"
import ListItem from "@/components/list-item"
import NavAvatar from "@/components/nav-avatar"
import Space from "@/components/space"
import { Session, SessionDashboardTab } from "@/redux/_session.types"
import logout from "@/utils/logout.utils"
import cookie from "react-cookies"
import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react"
import MenuAccountsWidget from "@/widgets/menu-accounts.widget"
import AccountLogoWidget from "@/widgets/account-logo.widget"
import backToSupervisor from "@/utils/back-to-supervisor.utils"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import HomeWelcomeModal from "@/modals/home-welcome.modal"
import { RotateInDownLeft, ZoomIn } from "@/utils/animations.utils"
import { NavigateFunction, useNavigate, useLocation, Link as RouterLink } from "react-router-dom"
import AccountExpiredModal from "@/modals/account-expired.modal"
import Chip from "@/components/chip"
import ContextMenu from "@/components/context-menu"
import ModalConfirm from "@/components/modal-confirm"
import { store, useAppDispatch, useAppSelector, RootState } from "@/core/root-reducer"
import { sessionEdit } from "@/redux/_session.actions"
import { roleFetch } from "@/redux/role.actions"
import { roleActions } from "@/redux/role.reducers"
import { accountCleanDemo } from "@/redux/account.actions"
import { ACCOUNT_DEMO } from "@/redux/account.types"
import { STATUS_SAVING } from "@/redux/_status.types"
import { toast } from "react-toastify"
import establishmentStore from "@/entities/establishment/model/establishment.store"
import SaveToast from "@/components/save-toast"
import SearchBar from "@/components/search-bar"
import { ESTABLISHMENT_TABS } from "@/nav/nav.establishment"
import { PREVENTION_TABS } from "@/nav/nav.prevention"

const STATUS_SELECTOR = (state: RootState) => [
  state.physicalRisk.status,
  state.population.status,
  state.site.status,
  state.preventionAction.status,
  state.riskFamily.status,
  state.establishment.status,
  state.document.status,
  state.map.status
]
import env from "@/env"
import { getActionPlanAccess, hasActionPlanScope } from "@/utils/observer-scope-options.utils"

interface StateProps extends WithTranslation {
  _session: Session
}

interface OwnProps {
  children: any //Content inside the page
}

type Props = StateProps & OwnProps

export interface NavItem {
  //Chemins qui allument l'item. A renseigner quand le 1er segment de l'URL ne suffit pas a
  //distinguer deux entrees du menu (ex : "Risques" et "Prevention" vivent tous deux sous /risks)
  activePathnames?: string[]
  text: string
  icon: IconDefinition
  pathname: string
  subItems?: NavSubItem[]
  onClick?: () => void
  hideText?: boolean
  hideTooltip?: boolean
}

interface NavSubItem {
  text: string
  label?: ReactNode
  icon?: IconDefinition
  pathname: string
  onClick?: () => void
}

/** Les onglets d'une section sont declares par sa nav (nav.establishment, nav.prevention) : le menu
 *  global les reprend en sous-menu au lieu de les redeclarer. */
function toNavSubItems(tabs: SessionDashboardTab[]): NavSubItem[] {
  return tabs.map((tab) => ({ text: tab.label, icon: tab.icon, pathname: tab.id }))
}

/**
 * RISKS / SUPERADMIN
 * List all establishemnt, manage scales and other admins
 */

export const establishmentsNavItem: NavItem = {
  text: "Établissements",
  pathname: "/risks/establishments",
  icon: faBuilding
}

export const adminsNavItem: NavItem = {
  text: "Administrateurs",
  pathname: "/risks/admins",
  icon: faUserShield
}

export const scalesNavItem: NavItem = {
  text: "Réglages prévention",
  pathname: "/risks/scales",
  icon: faSliders
}

/**
 * RISKS / CONTRIBUTOR - FIRST TAB
 * Risk cartography
 */

export const risksSitesNavItem: NavItem = {
  text: "Lieux de travail",
  pathname: "/risks/sites",
  icon: faLocationDot,
  hideText: true
}

export const risksPopulationsNavItem: NavItem = {
  text: "Fonctions",
  pathname: "/risks/populations",
  icon: faUserFriends,
  hideText: true
}

/**
 * RISKS / CONTRIBUTOR - SECOND TAB
 * Actions
 */

const actionsNavItem: NavItem = {
  activePathnames: PREVENTION_TABS.map((tab) => tab.id),
  text: "Prévention",
  pathname: "/risks/calendar",
  icon: faCircleCheck,
  subItems: toNavSubItems(PREVENTION_TABS)
}

/**
 * RISKS / CONTRIBUTOR - THIRD TAB
 * Documents
 */

export const risksDocumentNavItem: NavItem = {
  text: "Documents",
  pathname: "/risks/documents",
  icon: faFileAlt
}

/**
 * RISKS / CONTRIBUTOR - LAST TAB
 * Establishment properties
 */

const establishmentNavItem: NavItem = {
  text: "Établissement",
  pathname: "/risks/establishment",
  icon: faBuilding,
  hideText: true
}

/**
 * RISKS / CONTRIBUTOR - ALL TABS
 */
const contributorNavItems: NavItem[] = [
  actionsNavItem,
  risksDocumentNavItem
]
// Preserve expansion across route changes, which remount NavApp.
let previousRolesMenu = { accountId: "", expanded: false }

function NavApp(props: Props) {
  const menuTopRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const menu = menuTopRef.current
    if (!menu) return

    const updateCompactMenu = () => {
      // Mesurer les libellés sur une ligne, même quand le menu est réduit,
      // pour pouvoir les réafficher sans oscillation entre les deux modes.
      menu.classList.remove("menutop-compact")
      menu.classList.add("menutop-measuring")
      const requiredWidth = Array.from(menu.children).reduce((total, child) => {
        const style = getComputedStyle(child)
        return total + child.getBoundingClientRect().width + parseFloat(style.marginLeft) + parseFloat(style.marginRight)
      }, 0)
      const style = getComputedStyle(menu)
      const availableWidth = menu.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight)
      menu.classList.remove("menutop-measuring")
      menu.classList.toggle("menutop-compact", Math.round(requiredWidth) > availableWidth)
    }

    updateCompactMenu()
    //Mesure a la frame suivante : changer la mise en page pendant la notification
    //declenche l'erreur "ResizeObserver loop completed with undelivered notifications"
    let frame = 0
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(updateCompactMenu)
    })
    observer.observe(menu)
    Array.from(menu.children).forEach((child) => observer.observe(child))
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [])

  const { t } = props
  const dispatch = useAppDispatch()
  const navigate: NavigateFunction = useNavigate()
  const location = useLocation()
  const roles = useAppSelector((state) => state.roles)
  const rolesEnabled = props._session.userRole === "ADMIN" && props._session.accountOptions.roleManagementEnabled
  const reduceMotion = useReducedMotion()
  const rolesMenuExpanded = Boolean(rolesEnabled && location.pathname.startsWith("/adm/restricted-access") && roles.accountId === props._session.accountId && roles.list.length)
  const [rolesMenuInitiallyExpanded] = useState(() => previousRolesMenu.accountId === props._session.accountId && previousRolesMenu.expanded)

  useEffect(() => {
    previousRolesMenu = { accountId: props._session.accountId, expanded: rolesMenuExpanded }
  }, [props._session.accountId, rolesMenuExpanded])

  useEffect(() => {
    if (!rolesEnabled) {
      if (roles.accountId) dispatch(roleActions.reset())
      return
    }
    if (location.pathname.startsWith("/adm/")) dispatch(roleFetch())
  }, [dispatch, location.pathname, rolesEnabled, props._session.accountId, roles.accountId, roles.status])

  //Selectors
  const settingsExist = useAppSelector((state) => Boolean(state.account.active.accountRisksSettings))
  const accountRisksSettings = useAppSelector((state) => state._session.accountRisksSettings)
  const establishment = useAppSelector(establishmentStore.selectors.active)
  const establishments = useAppSelector(establishmentStore.selectors.list)

  //Detect if onboarding modal is visible
  const searchParams: URLSearchParams = new URLSearchParams(window.location.search)
  const isWelcomeMode = searchParams.get("modal") === "welcome" && props._session.interfaceType !== "ADMIN_PARENT"

  //Detect if hover is supported (tablette for example)
  const isHoverSupported = window.matchMedia("(hover: hover)").matches

  //Compte expiré
  const isExpired =
    !props._session.supervisorId && props._session.accountLastSubscriptionEnd
      ? props._session.accountLastSubscriptionEnd < Date.now()
      : false

  //Sub items for tasks (an observer with his scope: all the actions and his own plans)
  const isActionPlanScope = hasActionPlanScope(props._session)
  const tasksItems = [
    { text: "tasks_actions_all", pathname: "/tasks/all", icon: faClipboardList },
    { text: "tasks_all", pathname: "/tasks/home", icon: faUserFriends },
    { text: "tasks_self", pathname: "/tasks/self", icon: faUser }
  ]
  //Inviting the managers comes with the editable access
  const observerTasksItems = tasksItems.filter(
    (item) => item.pathname !== "/tasks/home" || getActionPlanAccess(props._session) === "scopeWrite"
  )

  //Superadmin risks connected in one establishment
  //Show connected establishment in the menu + back button + only icons
  const risksConnectedInEstablishment =
    props._session.featureConnected === "risks" &&
    props._session.establishmentId &&
    accountRisksSettings.allowMultipleEstablishments &&
    props._session.risksRole === "SUPERADMIN"

  const risksEstablishmentMode =
    props._session.featureConnected === "risks" &&
    props._session.risksInterfaceType === "ESTABLISHMENT" &&
    Boolean(props._session.establishmentId) &&
    settingsExist

  //Superadmin risks at accountRisksSettings level (multiple establishments)
  //Show the accountRisksSettings chip to switch into an establishment
  const risksAccountMode =
    props._session.featureConnected === "risks" &&
    props._session.risksInterfaceType === "ACCOUNT" &&
    accountRisksSettings.allowMultipleEstablishments &&
    props._session.risksRole === "SUPERADMIN"

  //Get admin menu
  const adminItems: NavItem[] = getAdminItems()

  //Get navItems for the current feature
  const navItems: NavItem[] = getNavItems()

  const [establishmentSearch, setEstablishmentSearch] = useState("")
  const normalizedEstablishmentSearch = establishmentSearch
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()

  //List of establishments shown in the chip dropdown
  const establishmentSubItems: NavSubItem[] = establishments
    .filter((e) =>
      e.name
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .includes(normalizedEstablishmentSearch)
    )
    .map((e) => ({
      text: e.name,
      pathname: "/home",
      onClick: () => confirmEnterEstablishment(e)
    }))

  let establishmentDropdownLabel = ""
  let establishmentDropdownItems: NavSubItem[] = []
  let isEstablishmentDropdownInverted = false

  //Connected inside an establishment > chip with "Retour" + establishments list
  if (risksConnectedInEstablishment) {
    establishmentDropdownLabel = establishment?.name ?? ""
    establishmentDropdownItems = [
      {
        text: `Retour vers ${props._session.accountName}`,
        label: (
          <>
            Retour vers&nbsp;<b>{props._session.accountName}</b>
          </>
        ),
        icon: faChevronLeft,
        pathname: "/home",
        onClick: () => confirmExitEstablishment()
      },
      ...establishmentSubItems
    ]
  }
  //AccountRisksSettings level > chip with accountRisksSettings name (bold, inverted colors), no "Retour"
  else if (risksAccountMode) {
    establishmentDropdownLabel = props._session.accountName
    establishmentDropdownItems = establishmentSubItems
    isEstablishmentDropdownInverted = true
  }

  //Current dialog
  const [currentModal, setCurrentModal] = useState<
    | "MODAL_WELCOME"
    | "MODAL_UNITS"
    | "MODAL_CLEAN_DEMO"
    | "MODAL_EXIT_ESTABLISHMENT"
    | "MODAL_ENTER_ESTABLISHMENT"
    | null
  >(isWelcomeMode ? "MODAL_WELCOME" : null)
  const [isDemoCleanLoading, setIsDemoCleanLoading] = useState(false)

  //Establishment targeted by the "enter establishment" confirmation modal
  const [establishmentToEnter, setEstablishmentToEnter] = useState<any>(null)

  //Animation for the buttom
  const [alternativeOwlAnimation, setAlternativeOwlAnimation] = useState<boolean>(false)

  //Show menu for switch feature
  const [showFeaturesMenu, setShowFeaturesMenu] = useState(false)
  const [showEstablishmentsMenu, setShowEstablishmentsMenu] = useState(false)

  const MAX_VISIBLE_ITEMS = 10
  const ITEM_HEIGHT = 40

  //Detect if user is log or not
  //If not log out
  useEffect(() => {
    if (props._session.authLevel < 1) {
      logout(true)
    }
  }, [props._session.authLevel])

  async function enterEstablishment(establishment) {
    const response = await dispatch(establishmentStore.thunks.enterContext(establishment)).unwrap()
    if (response.error) return

    navigate("/home")
  }

  function exitEstablishment() {
    cookie.remove("idtreeEstablishmentId", { path: "/" })
    dispatch(establishmentStore.actions.setActive(null))
    store.dispatch(sessionEdit("establishmentId", null))
    store.dispatch(sessionEdit("risksInterfaceType", "ACCOUNT"))
    navigate("/home")
  }

  function confirmExitEstablishment() {
    setCurrentModal("MODAL_EXIT_ESTABLISHMENT")
  }

  function confirmEnterEstablishment(establishment) {
    setEstablishmentToEnter(establishment)
    setCurrentModal("MODAL_ENTER_ESTABLISHMENT")
  }

  //Trigger animation when modal is closed
  function closeWelcomeModal() {
    setAlternativeOwlAnimation(true)
    setCurrentModal(null)
  }

  function returnToSupervisor() {
    if (props._session.accountId === ACCOUNT_DEMO.id) {
      setCurrentModal("MODAL_CLEAN_DEMO")
    } else {
      backToSupervisor()
    }
  }

  async function cleanDemoAndReturn() {
    setIsDemoCleanLoading(true)
    const response: any = await store.dispatch(accountCleanDemo())

    if (response.error) {
      setIsDemoCleanLoading(false)
      toast("Le nettoyage du compte Smartlink a échoué.", { type: "error" })
      return
    }

    backToSupervisor()
  }

  //Get items for the admin menu
  function getAdminItems() {
    let items: NavItem[] = []

    //For superadmin (consulting account)
    if (props._session.interfaceType === "ADMIN_PARENT") {
      items = items.concat([
        { text: "superadmin_users", pathname: "/superadmin/users", icon: faUsers },
        { text: "superadmin_templates", pathname: "/superadmin/templates", icon: faListCheck },
        { text: "utils_params", pathname: "/superadmin/settings", icon: faCog }
      ])
    }
    //For admin
    //> Roles (in admin or admin for one app)
    //> Users
    else if (props._session.userRole === "ADMIN" && props._session.featureConnected !== "risks") {
      items = items.concat([
        ...(props._session.accountOptions.roleManagementEnabled
          ? [
              { text: "roles_administrators", pathname: "/adm/roles", icon: faCrown },
              { text: "roles_restricted_access", pathname: "/adm/restricted-access", icon: faShieldHalved }
            ]
          : [{ text: "roles", pathname: "/adm/roles", icon: faCrown }]),
        { text: "users", pathname: "/adm/users", icon: faUserFriends },
        { text: "account_settings", pathname: "/adm/settings", icon: faCog }
      ])
    }

    return items
  }

  //Get items for the menu top
  function getNavItems() {
    //Init nav items
    const items: NavItem[] = []

    //Init home
    const homeItem = {
      text: props._session.featureConnected === "risks" ? "dashboard_overview" : "utils_home",
      pathname: "/home",
      icon: faHome,
      hideText: props._session.featureConnected !== "risks",
      hideTooltip: true
    }

    const risksHomeItem: NavItem = {
      activePathnames: ESTABLISHMENT_TABS.map((tab) => tab.id),
      text: "Risques",
      pathname: "/risks/phy",
      icon: faList,
      subItems: toNavSubItems(ESTABLISHMENT_TABS)
    }

    //Survey menu
    if (props._session.featureConnected === "survey") {
      //Init report item
      const reportItem = {
        text: "reports",
        pathname: "/report/list",
        icon: faFileAlt
      }

      //Admins accès
      if (props._session.userRole === "ADMIN") {
        //Add templates and results
        items.push({
          text: "templates",
          pathname: "/survey/templates",
          icon: faClipboardList
        })

        //Add project if admin
        if (props._session.interfaceType === "ADMIN") {
          items.push({
            text: "projects",
            pathname: "/project/list",
            icon: faPaperPlane
          })
        }

        //Add results if admin or account is not type consulting (not useful)
        if (!props._session.isSuperAccount || props._session.interfaceType === "ADMIN") {
          items.push({
            text: "dashboard_results",
            pathname: "/dashboard/home",
            icon: faChartBar
          })

          //Add reports
          items.push(reportItem)
        }
      } else if (props._session.userRole === "OBSERVER") {
        items.push(homeItem)
        if (!props._session.accountOptions.hideReportForObserver) {
          items.push(reportItem)
        }
      }

      //Add tasks
      if (props._session.modules.survey.task) {
        if (props._session.userRole === "ADMIN") {
          items.push({
            text: "tasks",
            pathname: "/tasks/all",
            icon: faTasks,
            subItems: tasksItems
          })
        } else if (isActionPlanScope) {
          items.push({
            text: "tasks",
            pathname: "/tasks/all",
            icon: faTasks,
            subItems: observerTasksItems
          })
        } else {
          items.push({
            text: "tasks",
            pathname: "/tasks/self",
            icon: faTasks
          })
        }
      }

      //Learning
      if (props._session.modules.survey.learning) {
        items.push({
          text: "E-learning",
          pathname: "/learning",
          icon: faFilm
        })
      }
    }

    //Risks menu only if accountRisksSettings exists
    else if (props._session.featureConnected === "risks" && settingsExist) {
      //Use nav
      //> Nav bar connected as accountRisksSettings
      if (props._session.risksInterfaceType === "ACCOUNT") {
        if (props._session.accountOptions.image) items.push(homeItem)
        items.push(establishmentsNavItem, adminsNavItem, scalesNavItem)
      } else if (props._session.establishmentId) {
        //> Nav bar as establishment superAdmin
        //The establishment chip is rendered on the right (next to the avatar)
        //Add all other items
        if (props._session.accountOptions.image) items.push(homeItem)
        items.push(risksHomeItem, ...contributorNavItems)
      }
    }

    return items
  }

  //Change feature
  //Dropdown chip
  function changeFeature(feature: string) {
    store.dispatch(sessionEdit("featureConnected", feature))
    setShowFeaturesMenu(false)
    navigate("/home")
  }

  function getSubItemIcon(icon: IconDefinition | undefined) {
    if (icon) {
      return (
        <FontAwesomeIcon
          className="mt-[2px] w-[30px] mr-[8px]"
          style={{ color: props._session.accountColors.brandPrimary }}
          icon={icon}
        />
      )
    } else {
      return null
    }
  }

  //Render a single nav item (used both in the top menu and on the right)
  function renderNavItem(item: NavItem, index: number | string) {
    const needsScroll = (item.subItems?.length ?? 0) > MAX_VISIBLE_ITEMS
    const subItems = item.subItems ?? []
    //Sans survol (tablette), rien ne se deroule : l'item mene a sa premiere page, sans caret.
    //Il s'allume alors sur chacune de ses pages, faute de menu pour les montrer.
    const isDropdownEnabled = subItems.length > 0 && isHoverSupported
    const [firstSubItem] = subItems
    const isFirstPageShortcut = Boolean(firstSubItem) && !isDropdownEnabled
    //Le survol ouvre le sous-menu ; le clic principal reste un raccourci vers sa premiere page.
    const primaryPathname = firstSubItem?.pathname ?? item.pathname
    const primaryOnClick = firstSubItem?.onClick ?? item.onClick

    return (
      <NavItem
        key={`${item.pathname}-${index}`}
        activePathnames={
          item.activePathnames ?? (isFirstPageShortcut ? subItems.map((subItem) => subItem.pathname) : undefined)
        }
        icon={item.icon}
        tooltip={item.hideTooltip ? undefined : t(item.text)}
        pathname={primaryPathname}
        title={item.hideText ? undefined : t(item.text)}
        onClick={primaryOnClick}>
        {isDropdownEnabled && (
          <div
            className={[
              "flex flex-col",
              needsScroll ? "overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-black/20" : ""
            ].join(" ")}
            style={needsScroll ? { maxHeight: `${MAX_VISIBLE_ITEMS * ITEM_HEIGHT}px` } : undefined}>
            {renderDropdownItems(subItems)}
          </div>
        )}
      </NavItem>
    )
  }

  function renderDropdownItems(items: NavSubItem[]) {
    return items.map((subItem, i) => (
      <ListItem
        key={i}
        isSmall
        isEditable
        onClick={subItem.onClick ?? (() => navigate(subItem.pathname))}
        className="flex items-center gap-3 h-[40px]">
        {subItem.icon && (
          <div className="w-5 flex-shrink-0 text-primary flex items-center justify-center">
            {getSubItemIcon(subItem.icon)}
          </div>
        )}
        <div className="flex-1 leading-tight line-clamp-2" title={t(subItem.text)}>
          {subItem.label ?? t(subItem.text)}
        </div>
      </ListItem>
    ))
  }

  function renderEstablishmentDropdown() {
    if (!risksConnectedInEstablishment && !risksAccountMode) return null

    const needsScroll = establishmentDropdownItems.length > MAX_VISIBLE_ITEMS

    return (
      <div
        className="rel flex mr-3"
        onMouseEnter={() => setShowEstablishmentsMenu(true)}
        onMouseLeave={() => setShowEstablishmentsMenu(false)}>
        <div
          className="flex h-full"
          onClick={() => setShowEstablishmentsMenu(true)}
          style={{
            color: isEstablishmentDropdownInverted
              ? props._session.accountColors.brandPrimary
              : props._session.accountColors.brandText
          }}>
          <div
            className="menutop-item-dropdown flex items-center my-auto cursor-pointer"
            style={{
              backgroundColor: isEstablishmentDropdownInverted ? "white" : undefined,
              border: isEstablishmentDropdownInverted ? undefined : "1px solid white",
              padding: "4px 16px",
              borderRadius: "16px",
              fontWeight: isEstablishmentDropdownInverted ? "bold" : undefined
            }}>
            <span>{establishmentDropdownLabel}</span>
            <FontAwesomeIcon icon={faCaretDown} />
          </div>
        </div>

        {showEstablishmentsMenu && (
          <ContextMenu positionRight onClose={() => setShowEstablishmentsMenu(false)}>
            <div className="mb-[14px]">
              <SearchBar searchText={establishmentSearch} onSearch={setEstablishmentSearch} />
            </div>
            <div
              className={[
                "flex flex-col",
                needsScroll ? "overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-black/20" : ""
              ].join(" ")}
              style={needsScroll ? { maxHeight: `${MAX_VISIBLE_ITEMS * ITEM_HEIGHT}px` } : undefined}>
              {renderDropdownItems(establishmentDropdownItems)}
            </div>
          </ContextMenu>
        )}
      </div>
    )
  }

  function renderAccountSettingsButton() {
    if (!risksAccountMode) return null

    return renderNavItem(
      {
        text: "Compte",
        pathname: "/adm/settings",
        icon: faCity,
        hideText: true
      },
      "account-settings"
    )
  }

  //List of menuLeftItems
  //Show for tasks or adm
  function getMenuLeftItems(): NavItem[] {
    const pathname = window.location.pathname
    if (pathname.startsWith("/tasks/") && props._session.userRole === "ADMIN") return tasksItems
    else if (pathname.startsWith("/tasks/") && isActionPlanScope) return observerTasksItems
    else if (pathname.startsWith("/adm/")) return adminItems
    else return []
  }

  //Detect if it is an admin page
  //menu left and specific margin
  function showMenuLeft() {
    return getMenuLeftItems().length > 0
  }

  function renderOwlFigure() {
    return (
      <div
        className="abs _hover"
        style={{
          right: 30,
          bottom: 20,
          height: 60,
          width: 60,
          borderRadius: 30,
          backgroundColor: props._session.accountColors.active,
          zIndex: 10
        }}
        onClick={() => setCurrentModal("MODAL_WELCOME")}>
        <img
          src={require("@/assets/assistant/owl_female_face.png")}
          alt={"owl assistant"}
          width={60}
          style={{ objectFit: "contain" }}
        />
      </div>
    )
  }

  return (
    <div className={props._session.connectedAsSuperadmin ? "consultant-navigation" : undefined}>
      <MenuAccountsWidget />

      {isExpired && <AccountExpiredModal expirationDate={props._session.accountLastSubscriptionEnd} />}

      {currentModal === "MODAL_WELCOME" && props._session.featureConnected === "survey" && (
        <HomeWelcomeModal onClose={() => closeWelcomeModal()} />
      )}

      {currentModal === "MODAL_EXIT_ESTABLISHMENT" && (
        <ModalConfirm
          onNo={() => setCurrentModal(null)}
          onYes={() => {
            setCurrentModal(null)
            exitEstablishment()
          }}
          textBold={`Quitter l'interface de l'établissement ${establishment?.name ?? ""}.`}
        />
      )}

      {currentModal === "MODAL_ENTER_ESTABLISHMENT" && (
        <ModalConfirm
          onNo={() => {
            setCurrentModal(null)
            setEstablishmentToEnter(null)
          }}
          onYes={() => {
            setCurrentModal(null)
            enterEstablishment(establishmentToEnter)
          }}
          textBold={`Souhaitez-vous vous connecter à l'établissement "${establishmentToEnter?.name ?? ""}" ?`}
        />
      )}

      {currentModal === "MODAL_CLEAN_DEMO" && (
        <ModalConfirm
          status={isDemoCleanLoading ? STATUS_SAVING : undefined}
          onNo={() => backToSupervisor()}
          onYes={() => cleanDemoAndReturn()}
          text="Souhaitez-vous nettoyer le compte Smartlink avant de retourner à l'espace gestionnaire IDTree ?"
          textBold="Le nettoyage supprime les projets, questionnaires et rapports créés pendant la démonstration."
        />
      )}

      {/** header */}
      <div
        ref={menuTopRef}
        className="flex page-navigation-menutop menutop-adaptive grey-bor"
        style={{
          background:
            "linear-gradient(90deg, " +
            props._session.accountColors.brandPrimary +
            " 0%, " +
            props._session.accountColors.brandSecondary +
            " 100%)",
          width: "100%"
        }}>
        <AccountLogoWidget />

        {
          /** loop on menu items */
          navItems.map((item, index) => renderNavItem(item, index))
        }

        <Space />

        {risksConnectedInEstablishment && (
          <NavItem
            icon={faChevronLeft}
            pathname=""
            tooltip={`Retour vers ${props._session.accountName}`}
            onClick={confirmExitEstablishment}
          />
        )}

        {risksEstablishmentMode && renderNavItem(establishmentNavItem, "establishment-settings")}

        {renderAccountSettingsButton()}

        {renderEstablishmentDropdown()}

        {!risksConnectedInEstablishment && adminItems.length > 0 && (
          <NavItem
            isMenuRightAlign
            icon={faCog}
            pathname={adminItems[0].pathname}
            title={
              props._session.accountName.length > 50
                ? props._session.accountName.slice(0, 45) + " [...]"
                : props._session.accountName
            }>
            {adminItems.map((adminItem, i) => (
              <div key={i}>
                <ListItem isSmall isEditable onClick={adminItem.onClick ?? (() => navigate(adminItem.pathname))}>
                  {getSubItemIcon(adminItem.icon)}
                  {t(adminItem.text)}
                </ListItem>
              </div>
            ))}
          </NavItem>
        )}

        <div style={{ width: 12 }} />

        <NavAvatar>
          {props._session.supervisorLevel > 0 ? (
            <a
              className="flex page-navigation-subitem"
              href={new URL("/pricing-idtree-f52fcfd2f738cd39b132d85594d2fc90.html", env.REACT_APP_URL_WEBSITE).href}
              target="_blank"
              rel="noopener noreferrer">
              <div className="context-menu-item">Configurateur de devis</div>
            </a>
          ) : (
            <div className="flex page-navigation-subitem" onClick={() => navigate("/adm/self")}>
              <div className="context-menu-item">{t("self_params")}</div>
            </div>
          )}
          <div className="flex page-navigation-subitem" onClick={() => logout(false)}>
            <div className="context-menu-item">{t("utils_logout")}</div>
          </div>
          {props._session.connectedAsSupervisor && (
            <div className="flex page-navigation-subitem" onClick={() => returnToSupervisor()}>
              <div className="context-menu-item">Retour (gestionnaire IDTree)</div>
            </div>
          )}
        </NavAvatar>

        {props._session.featuresList.length > 1 && (
          <div className="rel lightwhite feature-selector">
            {showFeaturesMenu && (
              <ContextMenu positionRight onClose={() => setShowFeaturesMenu(false)}>
                {props._session.featuresList.map((feature) => (
                  <ListItem
                    key={feature}
                    isSmall
                    onClick={() => changeFeature(feature)}
                    isEditable
                    isActive={feature === props._session.featureConnected}>
                    {t("feature_" + feature)}
                  </ListItem>
                ))}
              </ContextMenu>
            )}
            <Chip
              isOutlineColored
              color={props._session.accountColors.brandPrimary}
              onClick={() => setShowFeaturesMenu(true)}>
              {t("feature_" + props._session.featureConnected)} <FontAwesomeIcon icon={faCaretDown}></FontAwesomeIcon>
            </Chip>
          </div>
        )}
      </div>

      <div className="page-navigation page-navigation-withheader">
        {props._session.accountLastSubscriptionEnd && props._session.accountLastSubscriptionEnd < Date.now() && (
          <div
            className="flex flex-dcol"
            style={{
              width: "100%",
              height: 40,
              backgroundColor: "#eb5a46", //"#df1324",
              color: "white",
              zIndex: 100
            }}>
            <Space />
            <div
              className="_hover"
              style={{
                marginLeft: props._session.supervisorLevel > 0 ? 62 : 10
              }}>
              <b>
                {t("account_subscription_expired_on", {
                  date: new Date(props._session.accountLastSubscriptionEnd).toLocaleDateString()
                })}
              </b>
            </div>
            <Space />
          </div>
        )}

        <div className="flex">
          {props._session.connectedAsSuperadmin && <div style={{ width: "52px", flexShrink: 0 }} />}
          {showMenuLeft() && (
            <div className="page-navigation-menuleft">
              {getMenuLeftItems().map((page) => (
                <div key={page.pathname}>
                  <div onClick={() => navigate(page.pathname)} className="page-navigation-menuleft-item">
                    <span
                      className={
                        location.pathname.indexOf(page.pathname) > -1 ? "page-navigation-menuleft-item-active" : ""
                      }>
                      {getSubItemIcon(page.icon)}
                      {t(page.text)}
                    </span>
                  </div>
                  {/** La liste des roles ne se deplie que sur la page Acces restreints. */}
                  {page.pathname === "/adm/restricted-access" && rolesEnabled && roles.accountId === props._session.accountId && (
                    <motion.div
                      initial={{ height: rolesMenuInitiallyExpanded ? "auto" : 0 }}
                      animate={{ height: rolesMenuExpanded ? "auto" : 0 }}
                      transition={{ duration: reduceMotion ? 0 : 0.2, ease: "easeInOut" }}
                      style={{ overflow: "hidden", pointerEvents: rolesMenuExpanded ? undefined : "none" }}
                      aria-hidden={!rolesMenuExpanded}>
                      {roles.list.map((role, index) => (
                        <RouterLink
                          key={role.id}
                          tabIndex={rolesMenuExpanded ? 0 : -1}
                          to={`/adm/restricted-access/${role.id}`}
                          className="page-navigation-menuleft-item"
                          style={{ display: "block", padding: "8px 10px 8px 48px", marginBottom: index === roles.list.length - 1 ? 16 : undefined }}
                          aria-current={location.pathname === `/adm/restricted-access/${role.id}` ? "page" : undefined}>
                          <div
                            className={
                              "flex grey-t" +
                              (location.pathname === `/adm/restricted-access/${role.id}`
                                ? " page-navigation-menuleft-item-active"
                                : "")
                            }>
                            <span className="flex1">{role.name}</span>
                            <span>
                              <FontAwesomeIcon icon={faUser} />
                              &nbsp;{role.usersCount}
                            </span>
                          </div>
                        </RouterLink>
                      ))}
                    </motion.div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div
            id="page-navigation-scrollable"
            className="flex1 page-navigation-children page-navigation-scrollable flex flex-dcol"
            style={{ height: "calc(100vh - 88px)" }}>
            <div className="flex">
              {!showMenuLeft() && <Space />}
              <div className={showMenuLeft() ? "flex1" : ""} style={{ width: showMenuLeft() ? undefined : "1000px" }}>
                {props.children}
              </div>
              {!showMenuLeft() && <Space />}
            </div>

            <div className="height-60" />
          </div>
        </div>
      </div>

      {window.location.pathname.startsWith("/risks") && <SaveToast selector={STATUS_SELECTOR} />}

      {props._session.userRole === "ADMIN" &&
        props._session.interfaceType === "ADMIN" &&
        props._session.featureConnected === "survey" &&
        ["/home", "/survey/templates"].includes(window.location.pathname) &&
        currentModal !== "MODAL_WELCOME" &&
        (alternativeOwlAnimation ? (
          <RotateInDownLeft>
            <ZoomIn>{renderOwlFigure()}</ZoomIn>
          </RotateInDownLeft>
        ) : (
          <div>{renderOwlFigure()}</div>
        ))}
    </div>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session
})

export default connect(mapStateToProps)(withTranslation()(NavApp))
